import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useParams } from 'react-router-dom';
import { formatOfflineMessage } from './checkout/formatOfflineMessage';
import { clearOrderSuccessPayload, readOrderSuccessPayload } from './checkout/orderSuccessStorage';
import { formatINR } from './checkout/formatINR';
import StoreTrustBadges from './checkout/StoreTrustBadges';
import { useSiteSettings } from './SiteSettingsContext';
import { trackPurchase } from './analytics';

const DEFAULT_OFFLINE_MSG =
  'We have received your order {order_number}. Payment is pending — our team will confirm your order and contact you with UPI/bank details and delivery updates, usually within 1–2 business days.';

export default function OrderSuccessPage() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { settings: site } = useSiteSettings();
  const [offlineTemplate, setOfflineTemplate] = useState('');

  const stored = useMemo(() => {
    const fromNav = location.state?.orderNumber ? location.state : null;
    const fromStore = readOrderSuccessPayload();
    if (fromNav && String(fromNav.orderNumber) === String(orderNumber || '')) return fromNav;
    if (fromStore && String(fromStore.orderNumber) === String(orderNumber || '')) return fromStore;
    return fromStore || fromNav;
  }, [location.state, orderNumber]);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const offlineFromQuery = query.get('offline') === '1' || query.get('pending') === '1';
  const paidOnline = stored?.paidOnline === true;
  // Without session payload (bookmark/refresh), assume pay-later pending — not “paid online”.
  const offline = stored ? !paidOnline : offlineFromQuery || true;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetch('/api/checkout_config.php')
      .then((r) => r.json())
      .then((d) => {
        if (d?.ok && d.offline_order_message) {
          setOfflineTemplate(String(d.offline_order_message));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (orderNumber && stored) {
      const t = window.setTimeout(() => clearOrderSuccessPayload(), 30 * 60 * 1000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [orderNumber, stored]);

  useEffect(() => {
    if (stored?.orderNumber) {
      trackPurchase(stored);
    }
  }, [stored]);

  const bodyText = offline
    ? formatOfflineMessage(offlineTemplate || DEFAULT_OFFLINE_MSG, orderNumber)
    : site.store_copy?.order_success_paid_message ||
      'Payment received. We are preparing your order and will email you when it ships.';

  const showTotals = stored && (stored.subtotal != null || stored.total != null);

  if (!orderNumber) {
    return (
      <div className="store-page order-success-page store-page-enter">
        <div className="container store-page__container">
          <div className="store-success">
            <h1 className="store-page__title">Order not found</h1>
            <Link to="/shop" className="btn btn-primary rounded-pill store-cta">
              Continue shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="store-page order-success-page store-page-enter">
      <Helmet>
        <title>{site.store_copy?.seo_order_success_title || 'Order confirmed | Saloni Designs'}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container store-page__container">
        <div className="store-success order-success-card order-success-card--celebrate">
          <div
            className={`store-success__icon${offline ? ' store-success__icon--pending' : ''}`}
            aria-hidden
          >
            ✓
          </div>
          <p className="store-page__lead mb-2">Thank you</p>
          <h1 className="store-page__title mb-2">
            {offline ? 'Thank you — order received' : 'Payment successful'}
          </h1>

          <p className="store-success__order" aria-label="Order number">
            Order #{orderNumber}
          </p>

          {showTotals ? (
            <div className="order-success-totals" role="status">
              <div className="order-success-totals__row">
                <span>Items</span>
                <span>{formatINR(stored.subtotal ?? 0)}</span>
              </div>
              <div className="order-success-totals__row">
                <span>Delivery</span>
                <span>{Number(stored.shipping) > 0 ? formatINR(stored.shipping) : 'Free'}</span>
              </div>
              <div className="order-success-totals__row order-success-totals__row--total">
                <span>Total</span>
                <span>{formatINR(stored.total ?? 0)}</span>
              </div>
            </div>
          ) : null}

          <p className="order-success__message text-muted mb-0">{bodyText}</p>

          {!stored ? (
            <p className="small text-muted mt-2 mb-0">
              If you already paid online, check your email or contact us with order #{orderNumber}.
            </p>
          ) : null}

          <div
            className="order-success-guest-help alert alert-light border small text-start mt-3 mx-auto"
            style={{ maxWidth: '26rem' }}
            role="note"
          >
            <p className="fw-semibold text-body mb-2">Find this order later</p>
            <ul className="mb-2 ps-3">
              <li>
                <Link to="/track-order">Track order</Link> with <strong>#{orderNumber}</strong> and your checkout email
                or phone — no sign-in needed.
              </li>
              {stored?.guestSignedIn ? (
                <li>
                  You are signed in on this device. Use <Link to="/account">My account</Link> to see all orders.
                </li>
              ) : (
                <li>
                  <Link to="/account">My account</Link> shows orders when you are signed in on this device.
                </li>
              )}
              <li>
                On a new device? Use <Link to="/forgot-password">Forgot password</Link> with your checkout email to set
                a password, then sign in with email.
              </li>
            </ul>
            {stored?.guestEmail ? (
              <p className="mb-0 text-muted">Confirmation sent to {stored.guestEmail}.</p>
            ) : null}
          </div>

          {offline ? (
            <div className="order-success__steps small text-muted mt-3 text-start mx-auto" style={{ maxWidth: '22rem' }}>
              <p className="fw-semibold text-body mb-2">What happens next</p>
              <ol className="ps-3 mb-0">
                <li className="mb-1">We review your order (you may see “payment pending” until we confirm).</li>
                <li className="mb-1">We contact you with payment instructions if needed.</li>
                <li>After payment, we pack and ship — tracking updates appear when your order ships.</li>
              </ol>
            </div>
          ) : (
            <p className="order-success__next small text-muted mt-3 mb-0">
              We will email you when your order ships. Track status anytime on{' '}
              <Link to="/track-order">Track order</Link>.
            </p>
          )}

          <div className="d-flex flex-wrap gap-2 justify-content-center mt-4">
            <Link to="/track-order" className="btn btn-primary rounded-pill px-4 store-cta">
              Track this order
            </Link>
            <Link to="/account" className="btn btn-outline-primary rounded-pill px-4">
              My account
            </Link>
            <a
              href={`${site.whatsapp_href || 'https://wa.me/919680384332'}?text=${encodeURIComponent(
                `Hi, I placed order #${orderNumber} on Saloni Designs. I have a question about my order.`
              )}`}
              className="btn btn-outline-secondary rounded-pill px-4"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp us
            </a>
            <Link to="/shop" className="btn btn-outline-secondary rounded-pill px-4">
              Continue shopping
            </Link>
          </div>
        </div>
        <div className="mt-4 mx-auto store-success-trust text-center">
          <StoreTrustBadges compact />
        </div>
      </div>
    </div>
  );
}
